/*
 * Multi-select leaf picker with a hand-rolled virtualiser — the DP Charts
 * page needs to work with plants that carry several thousand leaves, and
 * rendering every row up-front pins the main thread on scroll.
 *
 * Design notes:
 * - The picker is a panel embedded in the charts page (not a modal). It sits
 *   in a left column and drives the chart to its right. Modal picking (the
 *   old LeafPicker) only makes sense for single-select widget config.
 * - Virtualisation is a single scroll container with an inner spacer sized
 *   to `rows.length * ROW_HEIGHT`. Only rows within the visible window plus
 *   an overscan buffer are rendered. No dependency added — react-window
 *   would work but a self-contained ~40 lines is easier to reason about.
 *   Seit dem HMI-Datenpunkt-Waehler steht diese Rechnung in
 *   `components/useVirtualRows.ts`, unveraendert, aber geteilt.
 * - Filter ranks results: exact symbol matches sit on top, then prefix hits,
 *   then substring hits. All matches are case-insensitive across symbol,
 *   displayName, unit and node path. Abflachung und Rangfilter liegen in
 *   `components/datapointRows.ts`, wo auch der HMI-Waehler sie holt.
 * - Multi-select uses checkboxes. "Select all filtered" and "Clear selection"
 *   shortcuts sit above the list. Shift-click extends selection between two
 *   rows for bulk picks (matches the OS-file-picker mental model).
 */

import { useMemo, useState } from 'preact/hooks';
import type { PlantSchemaResponse, LeafDto } from '../../api/types';
import { flattenDatapointRows, rankFilterDatapointRows } from '../datapointRows';
import { useVirtualRows } from '../useVirtualRows';
import './MultiLeafPicker.css';

interface Props {
  schema: PlantSchemaResponse;
  selectedIds: string[];
  onChange(ids: string[]): void;
  /** Optional right-side element rendered in the picker header (e.g. a
   *  "Save preset" button). Keeps the picker a self-contained panel while
   *  letting the parent tuck adjacent controls in without another layout row. */
  headerExtra?: preact.ComponentChildren;
}

const ROW_HEIGHT = 34;
const OVERSCAN_ROWS = 6;

export function MultiLeafPicker({ schema, selectedIds, onChange, headerExtra }: Props) {
  // Abflachung und Rangfilter liegen seit dem HMI-Datenpunkt-Waehler in
  // `components/datapointRows.ts` — identische Rechnung, nur eine Kopie
  // weniger. `isChartable` bleibt hier, weil es eine Eigenheit der
  // Chart-Seite ist und keine des Waehlers.
  const rows = useMemo(() => flattenDatapointRows(schema, isChartable), [schema]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const [filter, setFilter] = useState('');
  const [lastAnchor, setLastAnchor] = useState<number | null>(null);
  // Controller-Filter, gleiches Muster wie im DP-Editor (PlantSchemaPage):
  // null = alle Devices sichtbar, sonst exakt einer. Sichtbarkeit des Pickers
  // ist rein CSS-getrieben (:focus-within + --pinned wenn was ausgewählt ist).
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const filtered = useMemo(
    () => rankFilterDatapointRows(rows, filter, deviceFilter),
    [rows, filter, deviceFilter],
  );

  const { scrollRef, startIndex, endIndex, totalHeight } = useVirtualRows(
    filtered.length, ROW_HEIGHT, OVERSCAN_ROWS,
  );
  const window = filtered.slice(startIndex, endIndex);

  function toggle(index: number, shiftKey: boolean) {
    const row = filtered[index];
    if (!row) return;
    const next = new Set(selectedSet);
    if (shiftKey && lastAnchor !== null && lastAnchor !== index) {
      const [lo, hi] = lastAnchor < index ? [lastAnchor, index] : [index, lastAnchor];
      // Range-select respects the current row's inverse state: if the anchor
      // was checked and the target is unchecked, we CHECK the range; vice
      // versa uncheck. The mental model matches OS file pickers.
      const shouldCheck = !selectedSet.has(row.leaf.id);
      for (let i = lo; i <= hi; i++) {
        const r = filtered[i];
        if (!r) continue;
        if (shouldCheck) next.add(r.leaf.id);
        else next.delete(r.leaf.id);
      }
    } else {
      if (next.has(row.leaf.id)) next.delete(row.leaf.id);
      else next.add(row.leaf.id);
    }
    setLastAnchor(index);
    onChange(Array.from(next));
  }

  function selectAllFiltered() {
    const next = new Set(selectedSet);
    for (const r of filtered) next.add(r.leaf.id);
    onChange(Array.from(next));
  }

  function clearSelection() {
    onChange([]);
  }

  return (
    <div class="hv-multi-picker">
      <div class="hv-multi-picker__head">
        <div class="hv-multi-picker__title-row">
          <div class="hv-multi-picker__title">Datenpunkte</div>
          <div class="hv-multi-picker__actions">
            {headerExtra}
          </div>
        </div>
        <div
          class={
            'hv-multi-picker__filter-group'
            + (deviceFilter !== null ? ' hv-multi-picker__filter-group--pinned' : '')
          }
        >
          <input
            type="search"
            class="hv-multi-picker__filter"
            placeholder="Filter: Symbol, Name, Einheit, Pfad…"
            value={filter}
            onInput={(e) => setFilter((e.currentTarget as HTMLInputElement).value)}
            aria-label="Datenpunkte filtern"
          />
          {schema.devices.length > 1 && (
            <div class="hv-multi-picker__device-picker" role="group" aria-label="Controller-Filter">
              <button
                type="button"
                class={
                  'hv-multi-picker__device-chip'
                  + (deviceFilter === null ? ' hv-multi-picker__device-chip--active' : '')
                }
                onClick={() => setDeviceFilter(null)}
                title="Alle Controller anzeigen"
              >
                Alle
              </button>
              {schema.devices.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  class={
                    'hv-multi-picker__device-chip'
                    + (deviceFilter === d.id ? ' hv-multi-picker__device-chip--active' : '')
                  }
                  onClick={() => setDeviceFilter(d.id)}
                  title={`${d.name ?? d.symbol}${d.protocol ? ' — ' + d.protocol : ''}`}
                >
                  {d.name ?? d.symbol}
                </button>
              ))}
            </div>
          )}
        </div>
        <div class="hv-multi-picker__meta">
          <span class="muted">
            {selectedIds.length} ausgewählt
            {filter
              ? ` · ${filtered.length} von ${rows.length} sichtbar`
              : ` · ${rows.length} Datenpunkte`}
          </span>
          <div class="hv-multi-picker__quick">
            <button
              type="button"
              class="hv-btn hv-btn--sm"
              disabled={filtered.length === 0}
              onClick={selectAllFiltered}
              title="Alle gefilterten Treffer auswählen"
            >
              Alle
            </button>
            <button
              type="button"
              class="hv-btn hv-btn--sm"
              disabled={selectedIds.length === 0}
              onClick={clearSelection}
              title="Auswahl leeren"
            >
              Leeren
            </button>
          </div>
        </div>
      </div>
      <div class="hv-multi-picker__list" ref={scrollRef} role="listbox" aria-multiselectable="true">
        {filtered.length === 0 && (
          <div class="hv-multi-picker__empty muted">
            {rows.length === 0 ? 'Keine Datenpunkte vorhanden.' : `Keine Treffer für „${filter}".`}
          </div>
        )}
        <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
          {window.map((r, i) => {
            const absoluteIndex = startIndex + i;
            const checked = selectedSet.has(r.leaf.id);
            return (
              <div
                key={r.leaf.id}
                class={`hv-multi-picker__row${checked ? ' hv-multi-picker__row--checked' : ''}`}
                style={{
                  position: 'absolute',
                  top: `${absoluteIndex * ROW_HEIGHT}px`,
                  height: `${ROW_HEIGHT}px`,
                  left: 0,
                  right: 0,
                }}
                role="option"
                aria-selected={checked}
                onClick={(e) => toggle(absoluteIndex, (e as MouseEvent).shiftKey)}
                title={`${r.deviceSymbol} · ${r.path}`}
              >
                <input
                  type="checkbox"
                  class="hv-multi-picker__cb"
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const target = e.currentTarget as HTMLInputElement;
                    // Change fires after click; keep it in sync with the row.
                    // For a plain (non-shift) change, we just flip the id.
                    const next = new Set(selectedSet);
                    if (target.checked) next.add(r.leaf.id);
                    else next.delete(r.leaf.id);
                    setLastAnchor(absoluteIndex);
                    onChange(Array.from(next));
                  }}
                />
                <div class="hv-multi-picker__main">
                  <div class="hv-multi-picker__name">
                    {r.leaf.displayName || r.leaf.symbol}
                  </div>
                  <div class="hv-multi-picker__sub muted">
                    {r.deviceSymbol} · {r.path}
                  </div>
                </div>
                <div class="hv-multi-picker__unit">
                  {r.leaf.unit ?? ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Was landet im Chart-Picker? Frueher eine Whitelist auf
 * Number/Integer/Float/Boolean — die hat aber Climatix-Werte (die der
 * Treiber als Int32/Enum/Bool typisiert) unsichtbar gemacht, obwohl sie
 * plotbar sind. Deshalb jetzt eine Blacklist: alles was NICHT eindeutig ein
 * Freitext ist, geht rein.
 *
 * - Bool/Boolean → step-after 0/1
 * - Enum/Flags/BitFlags → step-after auf dem Enum-Index
 * - Int/UInt jeder Groesse, Float/Double/Decimal, Real → normale Linie
 * - String/Text/Blob → raus, sonst rendert der Chart eine Nullen-Linie
 */
function isChartable(leaf: LeafDto): boolean {
  const t = (leaf.valueType ?? '').trim();
  if (!t) return false;
  const lower = t.toLowerCase();
  return lower !== 'string' && lower !== 'text' && lower !== 'blob' && lower !== 'json';
}
